import { baseApi, storefrontPath } from './baseApi'
import type { BookingSummary, CouponCheck, Slot, Staff } from '../types/storefront'

/** Laravel reads repeated `service_ids[]` params as an array, so the wizard
 *  never has to comma-join and the backend never has to split. */
function listParams(params: URLSearchParams, key: string, values: number[]) {
  for (const v of values) params.append(`${key}[]`, String(v))
}

export interface AvailabilityArgs {
  slug: string
  branchId: number
  /** Calendar date in the vendor timezone, `yyyy-MM-dd`. */
  date: string
  serviceIds: number[]
  /** Same order and length as serviceIds when present; 0 means "any staff". */
  staffIds?: number[]
  /** "Any specialist" for the whole visit means one person who can do it all. */
  sameStaff?: boolean
}

export interface AvailableDatesArgs {
  slug: string
  branchId: number
  /** `yyyy-MM` in the vendor timezone. */
  month: string
  serviceIds: number[]
  sameStaff?: boolean
}

export interface ValidateCouponArgs {
  slug: string
  code: string
  branchId: number
  serviceIds: number[]
  subtotal: number
}

export interface StaffForSelectionArgs {
  slug: string
  branchId: number
  serviceIds: number[]
}

export interface CreateBookingArgs {
  slug: string
  /** Echoed in the body as well as the header — see the endpoint comment. */
  idempotencyKey: string
  branchId: number
  /** ISO 8601 with the vendor offset, exactly as the slot was returned. */
  startsAt: string
  /**
   * `is_favorite_staff` is the heart from the specialist step, per service. It is
   * the field frezka's quick booking already sends and the bookings table already
   * has, so the appointment wears the same heart in the dashboard calendar
   * wherever it was booked from.
   */
  services: { service_id: number; staff_id: number | null; is_favorite_staff: boolean }[]
  /** "Any specialist" for the whole visit: one person, never split. */
  sameStaff: boolean
  customer: { name: string; phone: string; email: string | null }
  note: string | null
  couponCode: string | null
}

/** The dates endpoint may hand back a bare array or `{ dates: [...] }`; both
 *  are normalised here so no screen has to care which shape shipped. */
export function toDateList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String)
  if (raw && typeof raw === 'object' && Array.isArray((raw as { dates?: unknown }).dates)) {
    return ((raw as { dates: unknown[] }).dates).map(String)
  }
  return []
}

export const bookingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getStaffForSelection: build.query<Staff[], StaffForSelectionArgs>({
      query: ({ slug, branchId, serviceIds }) => {
        const params = new URLSearchParams({ branch_id: String(branchId) })
        listParams(params, 'service_ids', serviceIds)
        return storefrontPath(slug, `/staff?${params.toString()}`)
      },
      providesTags: ['Staff'],
    }),

    getAvailability: build.query<Slot[], AvailabilityArgs>({
      query: ({ slug, branchId, date, serviceIds, staffIds, sameStaff }) => {
        const params = new URLSearchParams({ branch_id: String(branchId), date })
        listParams(params, 'service_ids', serviceIds)
        if (staffIds?.length) listParams(params, 'staff_ids', staffIds)
        if (sameStaff) params.set('same_staff', '1')
        return storefrontPath(slug, `/availability?${params.toString()}`)
      },
      providesTags: ['Availability'],
    }),

    getAvailableDates: build.query<string[], AvailableDatesArgs>({
      query: ({ slug, branchId, month, serviceIds, sameStaff }) => {
        const params = new URLSearchParams({ branch_id: String(branchId), month })
        listParams(params, 'service_ids', serviceIds)
        if (sameStaff) params.set('same_staff', '1')
        return storefrontPath(slug, `/availability/dates?${params.toString()}`)
      },
      transformResponse: toDateList,
      providesTags: ['Availability'],
    }),

    validateCoupon: build.mutation<CouponCheck, ValidateCouponArgs>({
      query: ({ slug, code, branchId, serviceIds, subtotal }) => ({
        url: storefrontPath(slug, '/coupons/validate'),
        method: 'POST',
        body: { code, branch_id: branchId, service_ids: serviceIds, subtotal },
      }),
    }),

    /**
     * The idempotency key travels twice on purpose: as the conventional
     * `Idempotency-Key` header and as a body field. Whichever the backend
     * chooses to key on, a double tap or a retry collapses onto one booking.
     */
    createBooking: build.mutation<BookingSummary, CreateBookingArgs>({
      query: ({
        slug,
        idempotencyKey,
        branchId,
        startsAt,
        services,
        customer,
        note,
        couponCode,
        sameStaff,
      }) => ({
        url: storefrontPath(slug, '/bookings'),
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: {
          idempotency_key: idempotencyKey,
          branch_id: branchId,
          starts_at: startsAt,
          services,
          customer,
          note,
          coupon_code: couponCode,
          same_staff: sameStaff,
        },
      }),
      invalidatesTags: ['Booking', 'Availability'],
    }),
  }),
})

export const {
  useGetStaffForSelectionQuery,
  useGetAvailabilityQuery,
  useGetAvailableDatesQuery,
  useValidateCouponMutation,
  useCreateBookingMutation,
} = bookingApi
